// WebAuthn / Passkey client helpers
// Conversions between base64url and ArrayBuffer

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function b64urlToBuffer(b64url) {
  const pad = '='.repeat((4 - (b64url.length % 4)) % 4);
  const b64 = (b64url + pad).replace(/-/g, '+').replace(/_/g, '/');
  const bin = atob(b64);
  const buf = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) buf[i] = bin.charCodeAt(i);
  return buf.buffer;
}

function bufferToB64url(buf) {
  const bytes = new Uint8Array(buf);
  let str = '';
  for (let i = 0; i < bytes.length; i++) str += String.fromCharCode(bytes[i]);
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function prepareCreationOptions(options) {
  return {
    ...options,
    challenge: b64urlToBuffer(options.challenge),
    user: {
      ...options.user,
      id: b64urlToBuffer(options.user.id),
    },
    excludeCredentials: (options.excludeCredentials || []).map(c => ({
      ...c,
      id: b64urlToBuffer(c.id),
    })),
  };
}

function prepareRequestOptions(options) {
  return {
    ...options,
    challenge: b64urlToBuffer(options.challenge),
    allowCredentials: (options.allowCredentials || []).map(c => ({
      ...c,
      id: b64urlToBuffer(c.id),
    })),
  };
}

function serializeAttestation(cred) {
  return {
    id: cred.id,
    rawId: bufferToB64url(cred.rawId),
    type: cred.type,
    response: {
      clientDataJSON: bufferToB64url(cred.response.clientDataJSON),
      attestationObject: bufferToB64url(cred.response.attestationObject),
    },
    clientExtensionResults: cred.getClientExtensionResults ? cred.getClientExtensionResults() : {},
    authenticatorAttachment: cred.authenticatorAttachment,
  };
}

function serializeAssertion(cred) {
  return {
    id: cred.id,
    rawId: bufferToB64url(cred.rawId),
    type: cred.type,
    response: {
      clientDataJSON: bufferToB64url(cred.response.clientDataJSON),
      authenticatorData: bufferToB64url(cred.response.authenticatorData),
      signature: bufferToB64url(cred.response.signature),
      userHandle: cred.response.userHandle ? bufferToB64url(cred.response.userHandle) : null,
    },
    clientExtensionResults: cred.getClientExtensionResults ? cred.getClientExtensionResults() : {},
  };
}

export function isPasskeySupported() {
  return typeof window !== 'undefined'
    && 'PublicKeyCredential' in window
    && typeof window.PublicKeyCredential === 'function';
}

export async function registerPasskey(axios, deviceName = 'Passkey') {
  const beginRes = await axios.post(`${API}/auth/passkey/register/begin`);
  const publicKey = prepareCreationOptions(beginRes.data);
  const cred = await navigator.credentials.create({ publicKey });
  const payload = serializeAttestation(cred);
  payload.device_name = deviceName;
  const done = await axios.post(`${API}/auth/passkey/register/complete`, payload);
  return done.data;
}

export async function loginWithPasskey(axios, email = '') {
  const beginRes = await axios.post(`${API}/auth/passkey/login/begin`, { email });
  const publicKey = prepareRequestOptions(beginRes.data);
  const challengeId = beginRes.data.challengeId;
  const cred = await navigator.credentials.get({ publicKey });
  const assertion = serializeAssertion(cred);
  const done = await axios.post(`${API}/auth/passkey/login/complete`, {
    challengeId,
    credential: assertion,
  });
  return done.data; // { access_token, user }
}

export async function listPasskeys(axios) {
  const res = await axios.get(`${API}/auth/passkey/list`);
  return res.data.passkeys;
}

export async function deletePasskey(axios, passkeyId) {
  await axios.delete(`${API}/auth/passkey/${passkeyId}`);
}
